import { InfoModal } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, Button, type SxProps, type Theme, Typography } from '@mui/material';

import type { ExportReport } from './reports.config';

const {
  modules: { coachingDashboard },
} = appStrings;

export type ReportExportStatus = 'idle' | 'loading' | 'zero' | 'error';

export type ReportsModalViewProps = Readonly<{
  open: boolean;
  onClose: () => void;
  reports: readonly ExportReport[];
  selectedKey: string;
  onSelect: (key: string) => void;
  status: ReportExportStatus;
  onDownload: () => void;
}>;

export const ReportsModalView = ({
  open,
  onClose,
  reports,
  selectedKey,
  onSelect,
  status,
  onDownload,
}: ReportsModalViewProps) => {
  const selectedReport = reports.find((report) => report.key === selectedKey) ?? null;
  const isLoading = status === 'loading';
  const canDownload = selectedReport !== null && !isLoading;

  return (
    <InfoModal
      open={open}
      onClose={onClose}
      width="md"
      fitViewport
      featuredIcon={{
        iconName: 'Download01',
        size: 'lg',
        color: 'Colors/Foreground/fg-brand-primary',
      }}
      headerContent={
        <Box sx={headerSx}>
          <Typography variant="text-lg-semibold">{coachingDashboard.reports.modalTitle}</Typography>
          <Typography variant="text-sm-regular" sx={subtitleSx}>
            {coachingDashboard.reports.subtitle}
          </Typography>
        </Box>
      }
      bodyContent={
        <Box sx={bodySx}>
          <Box sx={cardListSx}>
            {reports.map((report) => {
              const isSelected = report.key === selectedKey;

              return (
                <Box
                  id={`coaching-dashboard-reports-option-${report.key}`}
                  key={report.key}
                  role="button"
                  tabIndex={0}
                  aria-pressed={isSelected}
                  onClick={() => onSelect(report.key)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onSelect(report.key);
                    }
                  }}
                  sx={reportCardSx(isSelected)}
                >
                  <Typography variant="text-md-semibold">{report.label}</Typography>
                  <Typography variant="text-sm-regular" sx={descriptionSx}>
                    {report.description}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {status === 'zero' && (
            <Typography
              id="coaching-dashboard-reports-zero-message"
              variant="text-sm-regular"
              sx={mutedTextSx}
            >
              {coachingDashboard.reports.zeroRows}
            </Typography>
          )}
          {status === 'error' && (
            <Box sx={errorRowSx}>
              <Typography
                id="coaching-dashboard-reports-error-message"
                variant="text-sm-regular"
                sx={errorTextSx}
              >
                {coachingDashboard.reports.retryError}
              </Typography>
              <Button
                id="coaching-dashboard-reports-retry-button"
                hierarchy="Tertiary color"
                size="sm"
                onClick={onDownload}
                disabled={!canDownload}
              >
                {coachingDashboard.reports.tryAgain}
              </Button>
            </Box>
          )}

          <Box sx={actionsSx}>
            <Button
              id="coaching-dashboard-reports-cancel-button"
              hierarchy="Secondary gray"
              size="md"
              onClick={onClose}
            >
              {coachingDashboard.reports.cancel}
            </Button>
            <Button
              id="coaching-dashboard-reports-download-button"
              hierarchy="Primary"
              size="md"
              onClick={onDownload}
              disabled={!canDownload}
            >
              {isLoading ? coachingDashboard.reports.preparing : coachingDashboard.reports.download}
            </Button>
          </Box>
        </Box>
      }
    />
  );
};

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  position: 'relative',
  zIndex: 1,
});

const subtitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const bodySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  flex: 1,
  minHeight: 0,
});

const cardListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
  gap: backrs.spacing['spacing-lg'],
  paddingX: backrs.spacing['spacing-lg'],
  paddingY: backrs.spacing['spacing-xs'],
  flex: 1,
  minHeight: 0,
  maxHeight: 384,
  overflowY: 'auto',
});

const reportCardSx =
  (isSelected: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: backrs.spacing['spacing-xs'],
    padding: backrs.spacing['spacing-2xl'],
    borderRadius: backrs.radius['radius-xl'],
    backgroundColor: backrs.colors['Colors/Background/bg-primary'],
    border: '1px solid',
    borderColor: isSelected
      ? backrs.colors['Colors/Border/border-brand']
      : backrs.colors['Colors/Border/border-secondary'],
    boxShadow: isSelected ? `0 0 0 1px ${backrs.colors['Colors/Border/border-brand']}` : 'none',
    cursor: 'pointer',
    textAlign: 'left',
    '&:hover': {
      backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
    },
  });

const descriptionSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
});

const mutedTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const errorRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-sm'],
});

const errorTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-error-primary'],
});

const actionsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-md'],
  justifyContent: 'flex-end',
  paddingTop: backrs.spacing['spacing-md'],
});
